import { useMemo } from 'react';
import { chain } from 'lodash';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { VehicleSearchItem } from '~services/vehicle/types';
import { ItemExtended } from '~services/item/types';

import { useSubscriptionFeatures } from './useSubscriptionFeatures';
import { useSelectedRows, SelectedRowsReturn } from './useSelectedRows';

export interface UseGenerateRoutesSelectedDataParams {
  vehiclesData: VehicleSearchItem[];
  ordersData: ItemExtended[];
  keysFields: { VEHICLE: string; ORDER: string };
  getDisabledVehicle: (currentVehicle: VehicleSearchItem) => boolean;
  getDisabledOrder: (currentOrder: ItemExtended) => boolean;
  isScheduleRouteEnabled?: boolean;
}

export interface UseGenerateRoutesSelectedDataReturn {
  vehicles: SelectedRowsReturn<VehicleSearchItem>;
  orders: SelectedRowsReturn<ItemExtended>;
}

export const useGenerateRoutesSelectedData = ({
  vehiclesData,
  ordersData,
  keysFields,
  getDisabledVehicle,
  getDisabledOrder,
  isScheduleRouteEnabled = false,
}: UseGenerateRoutesSelectedDataParams): UseGenerateRoutesSelectedDataReturn => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const nonSelectablesVehiclesIds = useMemo(
    () =>
      chain(vehiclesData)
        .filter(getDisabledVehicle)
        .map(keysFields.VEHICLE)
        .value(),
    [vehiclesData, getDisabledVehicle, keysFields.VEHICLE],
  );

  const modeSelectedVehicles = useMemo(() => {
    let mode: 'radio' | 'checkbox' = hasAccessToFeature(
      PlanFeaturesTypes.MgmVRP,
    )
      ? 'checkbox'
      : 'radio';

    if (isScheduleRouteEnabled) mode = 'radio';

    return mode;
  }, [hasAccessToFeature, isScheduleRouteEnabled]);

  const selectedVehicles = useSelectedRows<VehicleSearchItem>(
    keysFields.VEHICLE,
    [],
    [],
    nonSelectablesVehiclesIds,
    modeSelectedVehicles,
  );

  const selectablesOrders = useMemo(
    () =>
      chain(ordersData)
        .filter((row) => !getDisabledOrder(row))
        .value(),
    [ordersData, getDisabledOrder],
  );

  const selectablesOrdersIds = useMemo(
    () => chain(selectablesOrders).map(keysFields.ORDER).value(),
    [keysFields.ORDER, selectablesOrders],
  );

  const nonSelectablesOrdersIds = useMemo(
    () =>
      chain(ordersData).filter(getDisabledOrder).map(keysFields.ORDER).value(),
    [getDisabledOrder, keysFields.ORDER, ordersData],
  );

  const selectedOrders = useSelectedRows<ItemExtended>(
    keysFields.ORDER,
    selectablesOrders,
    selectablesOrdersIds,
    nonSelectablesOrdersIds,
  );

  return { vehicles: selectedVehicles, orders: selectedOrders };
};
